import { BLUE_DARK } from "@finway-group/shared/lib/consts"
import { Checkbox, Form, Modal } from "antd"
import React, { useEffect, useState } from "react"
import { Info } from "react-feather"
import { useTranslation } from "react-i18next"
import { SelfIssuedReceiptIcon } from "~Components/icons/SelfIssuedReceiptIcon"

interface SelfIssuedReceiptConfirmations {
    accurateInformation: boolean
    receiptUnobtainable: boolean
}

interface SelfIssuedReceiptConfirmationModalProps {
    open: boolean
    onConfirm: () => void
    onCancel: () => void
}

const initialConfirmations: SelfIssuedReceiptConfirmations = {
    accurateInformation: false,
    receiptUnobtainable: false,
}

export const SelfIssuedReceiptConfirmationModal = ({ open, onConfirm, onCancel }: SelfIssuedReceiptConfirmationModalProps) => {
    const { t } = useTranslation()
    const [confirmations, setConfirmations] = useState<SelfIssuedReceiptConfirmations>(initialConfirmations)
    const [showCheckboxErrors, setShowCheckboxErrors] = useState(false)

    const hasAllConfirmations = confirmations.accurateInformation && confirmations.receiptUnobtainable
    const shouldShowCheckboxErrors = showCheckboxErrors && !hasAllConfirmations

    const resetModalState = () => {
        setConfirmations(initialConfirmations)
        setShowCheckboxErrors(false)
    }

    useEffect(() => {
        if (open) {
            resetModalState()
        }
    }, [open])

    const getCheckboxHighlightClassName = (isChecked: boolean) => (shouldShowCheckboxErrors && !isChecked ? "rounded-lg border border-error bg-error-bg p-2" : undefined)

    const handleOk = () => {
        if (!hasAllConfirmations) {
            setShowCheckboxErrors(true)
            return
        }

        onConfirm()
        resetModalState()
    }

    const handleCancel = () => {
        onCancel()
        resetModalState()
    }

    const InfoBullet = ({ value }: { value: string }) => (
        <div className="flex flex-row items-start gap-10 py-8">
            <div className="pt-4">
                <Info color={BLUE_DARK} size={14} />
            </div>
            <span>{value}</span>
        </div>
    )

    return (
        <Modal open={open} onOk={handleOk} onCancel={handleCancel} className="ant-modal--small">
            <div className="flex flex-row items-start gap-10">
                <SelfIssuedReceiptIcon size={24} />
                <span>{t("confirm:self_issued_receipt.enable.title")}</span>
            </div>
            <div className="px-30">
                <InfoBullet value={t("confirm:self_issued_receipt.enable.bullet_1")} />
                <InfoBullet value={t("confirm:self_issued_receipt.enable.bullet_2")} />
                <InfoBullet value={t("confirm:self_issued_receipt.enable.bullet_3")} />
                <InfoBullet value={t("confirm:self_issued_receipt.enable.bullet_4")} />
                <InfoBullet value={t("confirm:self_issued_receipt.enable.bullet_5")} />
                {shouldShowCheckboxErrors && (
                    <div>
                        <span className="text-error">{t("validation:required")}</span>
                    </div>
                )}
                <div className={getCheckboxHighlightClassName(confirmations.accurateInformation)}>
                    <Form.Item required className="m-0">
                        <Checkbox
                            checked={confirmations.accurateInformation}
                            onChange={(event) =>
                                setConfirmations((previous) => ({
                                    ...previous,
                                    accurateInformation: event.target.checked,
                                }))
                            }
                        >
                            {t("input:request.self_issued_receipt.accurate_information")}
                        </Checkbox>
                    </Form.Item>
                </div>
                <div className={getCheckboxHighlightClassName(confirmations.receiptUnobtainable)}>
                    <Form.Item required className="m-0">
                        <Checkbox
                            checked={confirmations.receiptUnobtainable}
                            onChange={(event) =>
                                setConfirmations((previous) => ({
                                    ...previous,
                                    receiptUnobtainable: event.target.checked,
                                }))
                            }
                        >
                            {t("input:request.self_issued_receipt.receipt_unobtainable")}
                        </Checkbox>
                    </Form.Item>
                </div>
            </div>
        </Modal>
    )
}
